import axios from "axios"
import { ReactElement } from "react"

import { axiosInstance } from "../pages/Auth/components/axiosInterceptors"

import pdf_svg from "../assets/images/pdf_svg.svg"
import folder_svg from "../assets/images/folder_icon.png"
import excel_svg from "../assets/images/excel_svg.svg"
import no_image from "../assets/images/no_image_svg.svg"
import image_uploaded from "../assets/images/image_uploaded_svg.svg"

export const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
}

export const isValidNonMandatoryEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return email === "" || emailRegex.test(email)
}

export const validateLong = (value: string | number) => {
    return /^\d+\.\d{6,10}?$/.test(value as string)
}

export const isValidPhoneNumber = (phoneNumber: string | number) => {
    let trimmedValue = typeof phoneNumber === "number" ? phoneNumber?.toString()?.trim() : phoneNumber?.trim()
    return trimmedValue?.length === 10
}

export const isValidPincode = (pincode: string | number) => {
    let trimmedValue = typeof pincode === "number" ? pincode?.toString()?.trim() : pincode?.trim()
    return trimmedValue?.length === 6
}

export const isValidGST = (gstNo: string) => {
    return gstNo?.trim()?.length === 15
}

export const isValidUTRNumber = (utr: string) => {
    return utr?.trim()?.length <= 20
}

export const isValidPercentage = (value: string) => {
    let numericValue = parseFloat(value)
    return numericValue >= 0 && numericValue <= 100
}
export const contactTag = [
    { id: 0, title: "Business POC" },
    { id: 1, title: "Host POC" },
    { id: 2, title: "Vendor POC" },
    { id: 3, title: "Client POC" },
    { id: 4, title: "Host Admin" },
    { id: 5, title: "Vendor Manager" },
    { id: 6, title: "Engineer" },
    { id: 7, title: "POC" },
]

export const renderImageType = (docType: string) => {
    switch (docType) {
        case "excel":
            return excel_svg
        case "doc":
            return pdf_svg
        case "folder":
            return folder_svg
        case "image":
            return image_uploaded
        case null:
        case "None":
            return no_image
        default:
            return no_image
    }
}
export interface PresignedUrl {
    presigned_url: string
    file_url: string[] | string
    file_type: string
    report_id?: number
    doc_id?: number
}

interface UrlsResponse {
    urls: { urls: PresignedUrl[]; file_type: string }[]
}

export const uploadFilesToS3 = async (
    fileObj: Record<string, File[]>,
    ticket_id: string = "",
    directUpload = false
) => {
    try {
        const filePayload = Object.keys(fileObj).map(fileType => ({
            file_type: fileType,
            file_name: fileObj[fileType]?.map(file => file.name),
        }))

        const payload = {
            ticket_id: ticket_id,
            files: filePayload,
        }

        const response = await axiosInstance.post<UrlsResponse>("upload_documents", payload)
        const urls = response.data?.urls

        if (urls && urls.length > 0) {
            const promises: Promise<any>[] = []
            for (const fileType of Object.keys(fileObj)) {
                const files = fileObj[fileType]

                const urlSet = urls.find(u => u.file_type === fileType)
                if (urlSet) {
                    for (let i = 0; i <= files?.length - 1; i++) {
                        const uploadPromise = axios.put(urlSet.urls[i].presigned_url, files[i], {
                            headers: {
                                "Content-Type": files[i].type,
                            },
                        })
                        promises.push(uploadPromise)
                    }
                } else {
                    console.error(`No Url found for key: ${fileType}`)
                }
            }

            try {
                await Promise.all(promises)
                let finalPayload = {
                    ticket_id: ticket_id,
                    file_infos: urls
                        ?.map(u => u.urls)
                        ?.flat()
                        ?.map(i => ({
                            file_url: i.file_url[0],
                            report_id: i.report_id,
                            doc_id: i.doc_id,
                        })),
                }
                directUpload && (await submitFinalUploads(finalPayload))
                return urls
            } catch (error) {
                console.error("Error uploading files:", error)
                throw error
            }
        } else {
            console.error("No URLs returned from the server")
            throw new Error("No URLs returned from the server")
        }
    } catch (error) {
        console.error("Error uploading files:", error)
        throw error
    }
}

export const deleteFiles = async (ids: number | number[]) => {
    try {
        let payload = {
            ids: typeof ids === "number" ? [ids] : ids,
        }
        await axiosInstance.delete("delete_documents", {
            data: payload,
        })
    } catch (error) {
        console.error("File Deletion Failed!", error)
        throw error
    }
}

export type FileInfo = Omit<PresignedUrl, "presigned_url" | "file_type">

export const submitFinalUploads = async (payload: { ticket_id: string; file_infos: FileInfo[] }) => {
    try {
        await axiosInstance.put("submit_final_uploads", payload)
    } catch (error) {
        console.error("Error uploading files: ", error)
        throw error
    }
}

export const getTagType = (ticket_type: string) => {
    return ticket_type
        .split("_")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join("")
}

/**
 * Extracts text from the second child (`children[1]`) of the given React element.
 * This function assumes that the second child is plain text rather than another React element.
 *
 * @param value - A ReactElement whose second child is expected to be a string.
 * @returns The second child's text content, or an empty string if unavailable.
 */
export function extractTextFromElement(value: ReactElement) {
    const children = value?.props?.children
    // Sometimes instead of an array the children can be of type string
    return typeof children === "string" ? children : value?.props?.children[1] || ""
}

/**
 * Converts a date string in the format "day month year" (e.g. "16 May 2025") to the format "yyyy-mm-dd" (e.g. "2025-05-16")
 * @param dateString - The date string to convert
 * @returns The date string in the format "yyyy-mm-dd"
 */
export function convertToDateFormat(dateString: string) {
    const months: Record<string, number> = {
        Jan: 1,
        Feb: 2,
        Mar: 3,
        Apr: 4,
        May: 5,
        Jun: 6,
        Jul: 7,
        Aug: 8,
        Sep: 9,
        Oct: 10,
        Nov: 11,
        Dec: 12,
    }

    const [day, month, year] = dateString.split(" ")
    const monthNumber = months[month] // Get the month number from the months object

    // Format the date as yyyy-mm-dd
    const formattedDate = `${year}-${String(monthNumber).padStart(2, "0")}-${String(day).padStart(2, "0")}`
    return formattedDate
}

/**
 * @param func - The function to be throttled
 * @param delay - The delay in milliseconds
 * @returns A throttled version of the input function
 */
export function throttle(func: (...args: any[]) => void, delay: number) {
    let lastCall = 0
    return function (...args: any[]) {
        const now = Date.now()
        if (now - lastCall >= delay) {
            lastCall = now
            func(...args)
        }
    }
}

/**
 * @param func - The function to be debounced
 * @param wait - The debounce delay in milliseconds
 * @returns A debounced version of the input function
 */
export function debounce<T extends (...args: any[]) => void>(func: T, wait: number): (...args: Parameters<T>) => void {
    let timeout: ReturnType<typeof setTimeout>

    return function (this: any, ...args: Parameters<T>) {
        const context = this
        clearTimeout(timeout)
        timeout = setTimeout(() => {
            func.apply(context, args)
        }, wait)
    }
}

export const getFileNameFromPresignedUrl = (url: string) => {
    const match = url.match(/logistics_bulk_upload_\d+\/.+/)

    if (match) {
        const extracted = match[0]
        return extracted
    } else {
        return ""
    }
}

export function formatINRCurrency(amount: number | string): string {
    if (amount == null || isNaN(Number(amount))) return ""

    const [integerPart, decimalPart] = amount.toString().split(".")

    const lastThree = integerPart.slice(-3)
    const rest = integerPart.slice(0, -3)

    const formattedRest = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",")

    const result = (formattedRest ? formattedRest + "," : "") + lastThree

    return decimalPart ? result + "." + decimalPart : result
}

/**
 * convert yyyy-mm-dd to dd-mm-yyyy
 * @param dateStr
 * @returns date in dd-mm-yyyy format
 */
export function convertDateToDMY(dateStr: string) {
    // Regular expression to check yyyy-mm-dd format
    const regex = /^\d{4}-\d{2}-\d{2}$/

    if (!regex.test(dateStr)) {
        return dateStr
    }

    const [year, month, day] = dateStr.split("-")

    return `${day}-${month}-${year}`
}

/**
 * 
 * @param input - The date string to format in the format "Thu, 25 Dec 2025 00:00:00 GMT"
 * @returns The date string in the format "yyyy-mm-dd"
 */
export function formatGMTDateToYYYYMMDD(input: string) {
    const date = new Date(input);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`
}
/**
 * 
 * @param input - The date string to format in the format "Thu, 25 Dec 2025 00:00:00 GMT"
 * @returns The date string in the format "yyyy-mm-dd"
 */
export function formatGMTDateToDDMMYYYY(input: string) {
    const date = new Date(input);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    return `${day}-${month}-${year}`
}

/**
 * Convert snake case to Capitalize Case
 * @param input - The string to convert
 * @returns The string in capitalize case
 */
export function convertSnakeCaseToCapitalizeCase(input: string) {
    return input.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase());
}